import { LitElement, html, css } from 'lit';

export class UIOtp extends LitElement {
  static formAssociated = true;

  static properties = {
    name: { type: String },
    value: { type: String, reflect: true },
    length: { type: Number }, // Number of input fields
    disabled: { type: Boolean, reflect: true },
    required: { type: Boolean, reflect: true },
    alphanumeric: { type: Boolean }, // If true, allow letters and numbers; else only digits
    label: { type: String, reflect: true },
    
    // Internal States
    digits: { type: Array, state: true }
  };

  static styles = css`
    :host {
      display: inline-block;
      width: 100%;
      font-family: var(--ui-font-family);
    }

    .ui-label {
      display: block;
      margin-bottom: 6px;
      font-size: var(--ui-font-size-sm);
      font-weight: 500;
      color: var(--ui-text-muted);
      text-align: left;
    }

    .otp-container {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      gap: 10px;
      width: 100%;
      box-sizing: border-box;
    }

    .otp-input {
      width: 48px;
      height: 48px;
      border: 1px solid var(--ui-border);
      border-radius: var(--ui-radius-md);
      background-color: var(--ui-surface);
      color: var(--ui-text);
      font-family: inherit;
      font-size: 20px;
      font-weight: 600;
      text-align: center;
      outline: none;
      box-shadow: var(--ui-shadow-sm);
      transition: var(--ui-transition-fast);
      box-sizing: border-box;
      padding: 0;
    }

    .otp-input:hover:not(:disabled) {
      border-color: var(--ui-border-hover);
      background-color: var(--ui-surface-hover);
    }

    .otp-input:focus:not(:disabled) {
      border-color: var(--ui-border-focus);
      background-color: var(--ui-surface);
      box-shadow: 0 0 0 4px var(--ui-focus-ring);
    }

    .otp-input:disabled {
      cursor: not-allowed;
      opacity: 0.6;
      background-color: var(--ui-surface-active);
    }

    @media (max-width: 576px) {
      .otp-container {
        gap: 6px;
        justify-content: space-between;
      }
      .otp-input {
        width: 100%;
        max-width: 44px;
        height: 44px;
        font-size: 16px;
      }
    }
  `;

  constructor() {
    super();
    this.internals = this.attachInternals();
    this.name = '';
    this.value = '';
    this.length = 6;
    this.disabled = false;
    this.required = false;
    this.alphanumeric = false;
    this.label = '';

    this.digits = Array(this.length).fill('');
  }

  willUpdate(changedProperties) {
    if (changedProperties.has('length')) {
      const len = Math.max(1, Math.min(10, this.length || 6));
      if (this.digits.length !== len) {
        this.digits = Array(len).fill('');
      }
    }
    if (changedProperties.has('value')) {
      const valStr = String(this.value || '');
      this.digits = Array(this.length).fill('').map((_, i) => valStr[i] || '');
    }
  }

  get validity() { return this.internals.validity; }
  get validationMessage() { return this.internals.validationMessage; }
  get willValidate() { return this.internals.willValidate; }
  checkValidity() { return this.internals.checkValidity(); }
  reportValidity() { return this.internals.reportValidity(); }

  updated(changedProperties) {
    if (changedProperties.has('value') || changedProperties.has('required')) {
      this.internals.setFormValue(this.value);
      this._updateValidity();
    }
  }

  _updateValidity() {
    const isComplete = this.value && this.value.length === this.length;
    if (this.required && !isComplete) {
      this.internals.setValidity(
        { valueMissing: true },
        `Veuillez saisir un code complet de ${this.length} caractères.`,
        this.shadowRoot.querySelectorAll('.otp-input')[0]
      );
    } else {
      this.internals.setValidity({});
    }
  }

  _handleInput(idx, e) {
    const rawVal = e.target.value;
    // Keep only last character
    const char = rawVal.slice(-1);
    
    // Validate characters based on alphanumeric rule
    const regex = this.alphanumeric ? /^[a-zA-Z0-9]$/ : /^[0-9]$/;
    
    if (char && regex.test(char)) {
      this.digits[idx] = char;
      this.digits = [...this.digits];
      this._syncValue();
      
      // Auto focus next input
      if (idx < this.length - 1) {
        const inputs = this.shadowRoot.querySelectorAll('.otp-input');
        inputs[idx + 1]?.focus();
      }
    } else {
      // Revert if invalid character entered
      e.target.value = this.digits[idx];
    }
  }

  _handleKeyDown(idx, e) {
    const inputs = this.shadowRoot.querySelectorAll('.otp-input');
    
    if (e.key === 'Backspace') {
      if (this.digits[idx]) {
        // Clear current value
        this.digits[idx] = '';
        this.digits = [...this.digits];
        this._syncValue();
      } else if (idx > 0) {
        // Focus previous and clear
        this.digits[idx - 1] = '';
        this.digits = [...this.digits];
        this._syncValue();
        inputs[idx - 1]?.focus();
      }
      e.preventDefault();
    } else if (e.key === 'ArrowLeft' && idx > 0) {
      inputs[idx - 1]?.focus();
    } else if (e.key === 'ArrowRight' && idx < this.length - 1) {
      inputs[idx + 1]?.focus();
    }
  }

  _handlePaste(e) {
    e.preventDefault();
    if (this.disabled) return;

    const pasteData = (e.clipboardData || window.clipboardData).getData('text');
    const regex = this.alphanumeric ? /[a-zA-Z0-9]/g : /[0-9]/g;
    const matches = pasteData.match(regex) || [];
    
    // Fill digits array
    for (let i = 0; i < this.length; i++) {
      if (matches[i] !== undefined) {
        this.digits[i] = matches[i];
      }
    }
    
    this.digits = [...this.digits];
    this._syncValue();

    // Focus last filled index, or final input
    const focusIdx = Math.min(matches.length, this.length - 1);
    const inputs = this.shadowRoot.querySelectorAll('.otp-input');
    inputs[focusIdx]?.focus();
  }

  _syncValue() {
    const newVal = this.digits.join('');
    if (newVal !== this.value) {
      this.value = newVal;
      this.dispatchEvent(new CustomEvent('change', {
        detail: { value: newVal },
        bubbles: true,
        composed: true
      }));
    }
  }

  render() {
    return html`
      ${this.label ? html`<label class="ui-label" part="label">${this.label}</label>` : ''}
      <div class="otp-container">
        ${this.digits.map((digit, idx) => html`
          <input
            type="text"
            class="otp-input" part="input"
            .value="${digit}"
            ?disabled="${this.disabled}"
            @input="${(e) => this._handleInput(idx, e)}"
            @keydown="${(e) => this._handleKeyDown(idx, e)}"
            @paste="${this._handlePaste}"
            maxlength="2"
            inputmode="${this.alphanumeric ? 'text' : 'numeric'}"
          />
        `)}
      </div>
    `;
  }
}

if (!customElements.get('ui-otp')) {
  customElements.define('ui-otp', UIOtp);
}
